import React, { useState } from "react";
import { Box, Button, Sheet, Stack, Typography } from "@mui/joy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SourceIcon from "@mui/icons-material/Source";
import { theme } from "styles/theme";
import CustomModal from "components/common/CustomModal";
import GuideEditorModal from "./GuideEditorModal";
import { AddIcon } from "static/image/common/IconAssets";
import type { TaskEditorState } from "./utils/taskEditorUtils";
import { taskEditorScrollbarSx } from "./utils/taskEditorScrollbarSx";
import type { TaskBlocksPatch } from "./agent/types";
import { useAgentChatThread } from "./agent/useAgentChatThread";
import { useStickToBottomScroll } from "./agent/useStickToBottomScroll";
import { PreviousThreadsDropdown } from "./agent/PreviousThreadsDropdown";
import {
  AgentLivingLogo,
  AssistantTurnView,
  ChatInput,
  PresetScenariosBar,
  PRESET_SCENARIOS,
  UserBubble,
} from "./agent/components";
import type { PresetScenario } from "./agent/components";

interface AgentChatPanelProps {
  taskId?: string;
  currentTask?: TaskEditorState;
  onTaskPatch: (
    patch: TaskBlocksPatch,
    meta?: { version?: number; revision?: string }
  ) => void;
  onTurnStart?: () => void;
  onTurnEnd?: () => void;
  onRevertTurn?: (taskBefore: TaskEditorState) => void;
  // 마지막 요청 수정 시 활동지를 직전 상태로 복원하고 working_store PUT 완료까지 await.
  onRevertTaskBeforeEdit?: (taskBefore: TaskEditorState) => Promise<void>;
  // 스트리밍 중인 활동지 id 집합이 바뀔 때 상위로 보고 — 목록 인디케이터용.
  onStreamingTasksChange?: (taskIds: string[]) => void;
}

export default function AgentChatPanel({
  taskId,
  currentTask,
  onTaskPatch,
  onTurnStart,
  onTurnEnd,
  onRevertTurn,
  onRevertTaskBeforeEdit,
  onStreamingTasksChange,
}: AgentChatPanelProps) {
  const {
    turns,
    isStreaming,
    isLoadingHistory,
    send,
    editLastUserTurn,
    respondToApproval,
    clear,
    cancel,
    threadId,
    revertTurn,
    switchThread,
    streamingTaskIds,
  } = useAgentChatThread(taskId, onTaskPatch, {
    onTurnStart,
    onTurnEnd,
    revertTaskBefore: onRevertTaskBeforeEdit,
  });

  // 스트리밍 활동지 집합 변화를 상위(목록 인디케이터)로 전달. 배열 정체성은 매 렌더
  // 새로 만들어지므로 정렬된 key 문자열로 실제 변화일 때만 보고한다.
  const streamingKey = [...streamingTaskIds].sort().join(",");
  React.useEffect(() => {
    onStreamingTasksChange?.(streamingKey ? streamingKey.split(",") : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamingKey]);

  const handleRevertTurn = (turnId: string) => {
    const before = revertTurn(turnId);
    if (before && onRevertTurn) onRevertTurn(before);
  };
  // 채팅 초안(입력 중 텍스트)은 쓰레드별로 분리해 보관한다. AgentChatPanel 은
  // 활동지/쓰레드 전환 시 리마운트되지 않으므로(부모가 key 를 안 줌) 단일 input
  // state 하나는 모든 쓰레드가 공유돼 "전역" 처럼 따라다닌다. threadId 기준 맵으로
  // 들고, 아직 쓰레드가 없는 새 대화는 활동지별 임시 키로 분리한다.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const draftKey = threadId ?? (taskId ? `new:${taskId}` : "none");
  const input = drafts[draftKey] ?? "";
  const setInput = (v: string) =>
    setDrafts((prev) => ({ ...prev, [draftKey]: v }));
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [guideEditorOpen, setGuideEditorOpen] = useState(false);
  const { scrollRef, isAtBottom, hasOverflow, onScroll, scrollToBottom } =
    useStickToBottomScroll([turns, isStreaming]);

  const isDisabled = !taskId;

  // 마지막 turn 이 종료된 assistant 일 때만 직전 user 메시지를 수정할 수 있다 (streaming/승인대기 중 불가).
  const lastTurn = turns[turns.length - 1];
  const canEditLast =
    !isStreaming &&
    lastTurn?.role === "assistant" &&
    (lastTurn.status === "complete" ||
      lastTurn.status === "error" ||
      lastTurn.status === "cancelled");

  const handleSend = () => {
    if (isDisabled || isStreaming) return;
    const trimmed = input.trim();
    if (!trimmed) return;
    void send(trimmed, currentTask);
    setInput("");
  };

  // timeout 으로 끊긴 마지막 turn 의 "다시 시도" — Agent 가 thread context 를 보고
  // 끊긴 지점부터 이어가도록 고정 메시지로 새 turn 을 연다.
  const handleRetry = () => {
    if (isDisabled || isStreaming) return;
    void send("이어서 진행해주세요", currentTask);
  };

  const handlePresetPick = (scenario: PresetScenario) => {
    if (isDisabled || isStreaming) return;
    void send(scenario.message, currentTask);
    setInput("");
  };

  const showEmptyState = !isDisabled && !isLoadingHistory && turns.length === 0;

  return (
    <Sheet
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        borderTop: 0,
        borderBottom: 0,
        borderLeft: 0,
        borderRadius: 0,
        backgroundColor: theme.vars.palette.Background.Level1,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "12px",
          py: "8px",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          background: `linear-gradient(180deg, ${theme.vars.palette.Surface.Forward} 0%, ${theme.vars.palette.Background.Level1} 100%)`,
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            minWidth: 28,
            minHeight: 28,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AgentLivingLogo size={15} active />
        </Box>
        <Typography
          level="title-sm"
          sx={{
            flex: 1,
            fontSize: "13px",
            fontWeight: 700,
            color: theme.vars.palette.Content.Strong,
          }}
        >
          Agent
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={() => setGuideEditorOpen(true)}
          aria-label="가이드 편집"
          title="가이드 편집"
          sx={{
            background: "none",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            lineHeight: 0,
            color: theme.vars.palette.Content.Weak,
            "&:hover": { color: theme.vars.palette.Content.Medium },
          }}
        >
          <SourceIcon sx={{ fontSize: "18px" }} />
        </Box>
        {taskId && (
          <PreviousThreadsDropdown
            taskId={taskId}
            currentThreadId={threadId}
            onSelectThread={(tid) => void switchThread(tid)}
          />
        )}
        <Button
          size="sm"
          variant="Plain"
          startDecorator={<AddIcon size={12} />}
          onClick={() => setConfirmClearOpen(true)}
          disabled={isDisabled || turns.length === 0}
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            minHeight: "28px",
          }}
        >
          새 대화
        </Button>
      </Box>

      <Box
        ref={scrollRef}
        onScroll={onScroll}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          // 스크롤바 자리를 좌/우 양쪽에 예약해 인풋 영역과 가운데 정렬을 일치시킴.
          scrollbarGutter: "stable both-edges",
          ...taskEditorScrollbarSx,
          pt: "14px",
          pb: "24px",
          px: "12px",
          backgroundColor: theme.vars.palette.Background.Level1,
          position: "relative",
          // floating button 이 항상 컨테이너 bottom 에 sticky 하게 자리잡도록
          // flex column + 마지막 자식의 mt: auto 패턴 사용.
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isDisabled ? (
          <Typography
            level="body-sm"
            sx={{
              color: theme.vars.palette.Content.Weak,
              textAlign: "center",
              mt: "32px",
            }}
          >
            활동지를 선택하면 채팅이 활성화됩니다.
          </Typography>
        ) : isLoadingHistory ? (
          <LoadingState />
        ) : showEmptyState ? (
          <EmptyState onPick={handlePresetPick} />
        ) : (
          <Stack
            spacing={1.5}
            sx={{ maxWidth: "760px", mx: "auto", width: "100%" }}
          >
            {turns.map((turn, idx) => {
              const isLast = idx === turns.length - 1;
              if (turn.role === "user") {
                return (
                  <UserBubble
                    key={turn.id}
                    turn={turn}
                    editable={canEditLast && idx === turns.length - 2}
                    onEdit={(m) => void editLastUserTurn(m, currentTask)}
                  />
                );
              }
              return (
                <AssistantTurnView
                  key={turn.id}
                  turn={turn}
                  isLast={isLast}
                  isStreaming={isStreaming}
                  currentTask={currentTask}
                  onDecide={(decisions) =>
                    respondToApproval(turn.id, decisions, currentTask)
                  }
                  onRevertTurn={() => handleRevertTurn(turn.id)}
                  onRetry={isLast ? handleRetry : undefined}
                />
              );
            })}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          position: "relative",
          maxWidth: "760px",
          mx: "auto",
          width: "calc(100% - 24px)",
          mb: "12px",
          borderRadius: "16px",
          backgroundColor: theme.vars.palette.Surface.Forward,
          border: `1px solid ${theme.vars.palette.Border.Weak}`,
          boxShadow: "0 6px 20px rgba(15, 23, 42, 0.08)",
        }}
      >
        {hasOverflow && !isAtBottom && !showEmptyState && !isDisabled && (
          <Box
            component="button"
            type="button"
            onClick={scrollToBottom}
            aria-label="맨 아래로 이동"
            sx={{
              position: "absolute",
              // 인풋 floating 카드 바로 위 — 카드와 가까운 간격 유지.
              top: "-34px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 26,
              height: 26,
              borderRadius: "999px",
              backgroundColor: theme.vars.palette.Surface.Forward,
              border: `1px solid ${theme.vars.palette.Border.Weak}`,
              boxShadow: "0 2px 6px rgba(15, 23, 42, 0.08)",
              color: theme.vars.palette.Content.Strong,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              zIndex: 2,
              transition: "background-color 0.12s ease, color 0.12s ease",
              "&:hover": {
                backgroundColor: theme.vars.palette.Background.Level2,
                color: theme.vars.palette.Content.Highlight,
              },
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: "18px" }} />
          </Box>
        )}
        <PresetScenariosBar
          onPick={handlePresetPick}
          disabled={isDisabled || isStreaming}
        />

        <ChatInput
          value={input}
          onChange={setInput}
          onSubmit={handleSend}
          onStop={cancel}
          disabled={isDisabled}
          isStreaming={isStreaming}
          placeholder={
            isDisabled
              ? "활동지를 먼저 선택하세요"
              : isStreaming
                ? "응답 받는 중…"
                : "작업을 설명하세요 (Enter 전송, Shift+Enter 줄바꿈)"
          }
        />
      </Box>

      <CustomModal
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        title="새 대화"
        subtitle="지금까지의 대화를 마치고 새로 시작할까요? 이전 대화는 상단 '대화 내역' 에서 다시 열어볼 수 있어요."
        actions={[
          {
            label: "새 대화 시작하기",
            variant: "Solid",
            color: "primary",
            onClick: () => {
              clear();
              setConfirmClearOpen(false);
            },
          },
          {
            label: "취소",
            variant: "Plain",
            onClick: () => setConfirmClearOpen(false),
          },
        ]}
      />

      <GuideEditorModal
        open={guideEditorOpen}
        onClose={() => setGuideEditorOpen(false)}
      />
    </Sheet>
  );
}

function LoadingState() {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        py: "48px",
        textAlign: "center",
      }}
    >
      <AgentLivingLogo size={64} active />
      <Typography
        level="body-sm"
        sx={{
          color: theme.vars.palette.Content.Weak,
          fontSize: "12px",
        }}
      >
        대화 내역을 불러오는 중…
      </Typography>
    </Box>
  );
}

function EmptyState({ onPick }: { onPick: (s: PresetScenario) => void }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        py: "48px",
        textAlign: "center",
      }}
    >
      <AgentLivingLogo size={64} />
      <Stack spacing={0.5} alignItems="center">
        <Typography
          level="title-sm"
          sx={{
            fontWeight: 700,
            color: theme.vars.palette.Content.Strong,
            fontSize: "14px",
          }}
        >
          Agent 와 함께 활동지 제작
        </Typography>
        <Typography
          level="body-xs"
          sx={{
            color: theme.vars.palette.Content.Weak,
            maxWidth: "260px",
            fontSize: "12px",
            lineHeight: 1.5,
          }}
        >
          아래에서 시나리오를 선택하거나 직접 메시지를 입력해보세요.
        </Typography>
      </Stack>
      <Stack spacing={0.75} sx={{ width: "100%", maxWidth: "300px" }}>
        {PRESET_SCENARIOS.slice(0, 4).map((scenario) => (
          <Box
            key={scenario.id}
            onClick={() => onPick(scenario)}
            sx={{
              px: "12px",
              py: "10px",
              borderRadius: "8px",
              backgroundColor: theme.vars.palette.Surface.Forward,
              border: `1px solid ${theme.vars.palette.Border.Weak}`,
              cursor: "pointer",
              textAlign: "left",
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: theme.vars.palette.primary.solid,
                backgroundColor: theme.vars.palette.Background.Level1,
                transform: "translateY(-1px)",
                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              },
            }}
          >
            <Typography
              level="body-sm"
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: theme.vars.palette.Content.Strong,
              }}
            >
              {scenario.label}
            </Typography>
            <Typography
              level="body-xs"
              sx={{
                fontSize: "11px",
                color: theme.vars.palette.Content.Weak,
                mt: "2px",
              }}
            >
              {scenario.description}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
